import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { Referral, ReferralSummary } from '../types';
import {
  ListPagination,
  REFERRAL_STATUS_LABELS,
  ReferralStatusBadge,
  SummaryTile,
  formatReferralDate,
  useUrlFilters,
} from './referralUi';

const FILTER_KEYS = ['customer_search', 'status', 'start_date', 'end_date'] as const;
const INPUT = 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1';
const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

const CustomerCell: React.FC<{ id: number; name: string; code: string }> = ({ id, name, code }) => (
  <Link to={`/customers/${id}`} className="hover:underline whitespace-nowrap">
    <div className="text-sm font-medium text-gray-900">{name}</div>
    <div className="text-sm text-gray-500">{code}</div>
  </Link>
);

const ReferralsList: React.FC = () => {
  const { values: filters, page, setFilters, setPage } = useUrlFilters(FILTER_KEYS);
  const [searchDraft, setSearchDraft] = useState(filters.customer_search);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { customer_search, status, start_date, end_date } = filters;

  useEffect(() => {
    setSearchDraft(customer_search);
  }, [customer_search]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const query = new URLSearchParams();
        if (customer_search) query.append('customer_search', customer_search);
        if (status) query.append('status', status);
        if (start_date) query.append('start_date', start_date);
        if (end_date) query.append('end_date', end_date);
        query.append('page', String(page));
        query.append('per_page', '10');

        const res = await fetchWithAuth(`/referrals?${query.toString()}`);
        if (!res.ok) throw new Error('Failed to fetch referrals');

        const data = await res.json();
        setReferrals(data.referrals);
        setSummary(data.summary);
        setTotalPages(data.pagination.total_pages);
        setTotalCount(data.pagination.total_count);
      } catch (error) {
        console.error('Error fetching referrals:', error);
        setReferrals([]);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [customer_search, status, start_date, end_date, page]);

  const applySearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({ customer_search: searchDraft.trim() });
  };

  return (
    <>
      <form onSubmit={applySearch} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label htmlFor="referralSearch" className={LABEL}>Customer Search</label>
            <input
              id="referralSearch"
              type="text"
              value={searchDraft}
              onChange={e => setSearchDraft(e.target.value)}
              placeholder="Referrer or friend..."
              className={INPUT}
            />
          </div>
          <div>
            <label htmlFor="referralStatus" className={LABEL}>Status</label>
            <select id="referralStatus" value={status} onChange={e => setFilters({ status: e.target.value })} className={INPUT}>
              <option value="">All</option>
              {Object.entries(REFERRAL_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="referralStartDate" className={LABEL}>Referred From</label>
            <input
              id="referralStartDate"
              type="date"
              value={start_date}
              onChange={e => setFilters({ start_date: e.target.value })}
              className={INPUT}
            />
          </div>
          <div>
            <label htmlFor="referralEndDate" className={LABEL}>Referred To</label>
            <input
              id="referralEndDate"
              type="date"
              value={end_date}
              onChange={e => setFilters({ end_date: e.target.value })}
              className={INPUT}
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600">Found {totalCount} referral{totalCount === 1 ? '' : 's'}</p>
      </form>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryTile value={summary.total} label="Friends referred" />
          <SummaryTile value={summary.active} label="Active (first order paid)" valueClassName="text-green-700" />
          <SummaryTile value={summary.signed_up} label="Signed up, no paid order yet" />
          <SummaryTile value={summary.pending} label="Registered, phone not verified" valueClassName="text-amber-700" />
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className={TH}>Referred</th>
                <th className={TH}>Referrer</th>
                <th className={TH}>Friend</th>
                <th className={TH}>Status</th>
                <th className={TH}>Signed Up</th>
                <th className={TH}>First Paid Order</th>
                <th className={`hidden lg:table-cell ${TH}`}>Code Used</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">Loading referrals...</td></tr>
              ) : referrals.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-500">No referrals found</td></tr>
              ) : (
                referrals.map(referral => (
                  <tr key={referral.id} className="hover:bg-gray-50" data-testid={`referral-${referral.id}`}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{formatReferralDate(referral.created_at)}</td>
                    <td className="px-4 py-4">
                      <CustomerCell id={referral.referrer_id} name={referral.referrer_name} code={referral.referrer_code} />
                    </td>
                    <td className="px-4 py-4">
                      <CustomerCell id={referral.friend_id} name={referral.friend_name} code={referral.friend_code} />
                    </td>
                    <td className="px-4 py-4"><ReferralStatusBadge status={referral.status} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatReferralDate(referral.signed_up_at) || <span className="text-gray-500">Not yet</span>}
                    </td>
                    <td className="px-4 py-4 text-sm">
                      {referral.activating_order_id ? (
                        <>
                          <Link to={`/orders/${referral.activating_order_id}`} className="text-blue-600 hover:underline">
                            {referral.activating_order_reference}
                          </Link>
                          <div className="text-gray-500">{formatReferralDate(referral.activated_at)}</div>
                        </>
                      ) : (
                        <span className="text-gray-500">No paid order yet</span>
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-4 text-sm text-gray-500">{referral.invite_code_used}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ListPagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </>
  );
};

export default ReferralsList;
